import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Separator } from '../ui/separator';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../ui/tooltip';
import {
    Calculator,
    Loader2,
    RotateCcw,
    Save,
    TestTube,
    Info,
    TrendingUp,
    Truck,
    Fuel,
    Clock,
    DollarSign,
    ArrowRight,
    CheckCircle2,
    AlertCircle,
    Globe,
    Settings2
} from 'lucide-react';
import { toast } from '../ui/use-toast';
import {
    CategoryAPI,
    CategoryPricingConfigResponse,
    PricingConfigUpdateRequest,
    PricingTestResponse
} from '../../lib/api';
import { cn } from '../../lib/utils';

interface PricingConfigModalProps {
    isOpen: boolean;
    onClose: () => void;
    categoryId: string;
    categoryName: string;
    onConfigUpdated?: () => void;
}

export function PricingConfigModal({
    isOpen,
    onClose,
    categoryId,
    categoryName,
    onConfigUpdated
}: PricingConfigModalProps) {
    // Loading states
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);

    // Config data
    const [configData, setConfigData] = useState<CategoryPricingConfigResponse | null>(null);

    // Form state
    const [formData, setFormData] = useState<PricingConfigUpdateRequest>({
        perKmRate: 2.5,
        minimumCharge: 0,
        maximumCharge: null,
        fuelSurchargePercentage: 0,
        rushHourMultiplier: 1
    });

    // Validation errors
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Test calculator state
    const [testInputs, setTestInputs] = useState({
        distance: 25,
        quantity: 10,
        materialPrice: 100
    });
    const [testResult, setTestResult] = useState<PricingTestResponse | null>(null);

    // Reset confirmation dialog
    const [showResetDialog, setShowResetDialog] = useState(false);

    // Track if form has unsaved changes
    const [hasChanges, setHasChanges] = useState(false);

    // Fetch pricing config on mount
    useEffect(() => {
        if (isOpen && categoryId) {
            fetchPricingConfig();
        }
    }, [isOpen, categoryId]);

    const fetchPricingConfig = async () => {
        setLoading(true);
        try {
            const response = await CategoryAPI.getPricingConfig(categoryId);
            if (response.success) {
                setConfigData(response.data);
                // Initialize form with effective config
                const config = response.data.effectiveConfig;
                setFormData({
                    perKmRate: config.perKmRate,
                    minimumCharge: config.minimumCharge,
                    maximumCharge: config.maximumCharge,
                    fuelSurchargePercentage: config.fuelSurchargePercentage,
                    rushHourMultiplier: config.rushHourMultiplier
                });
                setHasChanges(false);
            }
        } catch (err) {
            console.error('Error fetching pricing config:', err);
            toast({
                title: "Error",
                description: "Failed to load pricing configuration.",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.perKmRate || formData.perKmRate <= 0) {
            newErrors.perKmRate = 'Per Km Rate must be greater than 0';
        }

        if (formData.minimumCharge !== undefined && formData.minimumCharge < 0) {
            newErrors.minimumCharge = 'Minimum Charge cannot be negative';
        }

        if (formData.maximumCharge !== undefined && formData.maximumCharge !== null && formData.maximumCharge < 0) {
            newErrors.maximumCharge = 'Maximum Charge cannot be negative';
        }

        if (formData.maximumCharge !== null && formData.minimumCharge !== undefined &&
            formData.maximumCharge !== undefined && formData.maximumCharge < formData.minimumCharge) {
            newErrors.maximumCharge = 'Maximum Charge must be greater than Minimum Charge';
        }

        if (formData.fuelSurchargePercentage !== undefined &&
            (formData.fuelSurchargePercentage < 0 || formData.fuelSurchargePercentage > 100)) {
            newErrors.fuelSurchargePercentage = 'Fuel Surcharge must be between 0 and 100';
        }

        if (formData.rushHourMultiplier !== undefined && formData.rushHourMultiplier < 1) {
            newErrors.rushHourMultiplier = 'Rush Hour Multiplier must be at least 1';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field: keyof PricingConfigUpdateRequest, value: string) => {
        let parsedValue: number | null = null;

        if (value === '' || value === 'null') {
            parsedValue = field === 'maximumCharge' ? null : 0;
        } else {
            parsedValue = parseFloat(value);
            if (isNaN(parsedValue)) parsedValue = 0;
        }

        setFormData(prev => ({
            ...prev,
            [field]: parsedValue
        }));
        setHasChanges(true);

        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setSaving(true);
        try {
            const response = await CategoryAPI.updatePricingConfig(categoryId, formData);
            if (response.success) {
                toast({
                    title: "Success",
                    description: "Pricing configuration updated successfully!",
                    variant: "default",
                });
                setHasChanges(false);
                await fetchPricingConfig();
                onConfigUpdated?.();
            }
        } catch (err) {
            console.error('Error saving pricing config:', err);
            toast({
                title: "Error",
                description: "Failed to save pricing configuration.",
                variant: "destructive",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        setShowResetDialog(false);
        setSaving(true);
        try {
            const response = await CategoryAPI.deletePricingConfig(categoryId);
            if (response.success) {
                toast({
                    title: "Success",
                    description: "Pricing configuration reset to global settings.",
                    variant: "default",
                });
                setHasChanges(false);
                await fetchPricingConfig();
                onConfigUpdated?.();
            }
        } catch (err: any) {
            console.error('Error resetting pricing config:', err);
            toast({
                title: "Error",
                description: err?.message || "Failed to reset pricing configuration.",
                variant: "destructive",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleTestCalculation = async () => {
        setTesting(true);
        try {
            const response = await CategoryAPI.testPricingConfig(categoryId, testInputs);
            if (response.success) {
                setTestResult(response.data);
            }
        } catch (err) {
            console.error('Error testing pricing:', err);
            toast({
                title: "Error",
                description: "Failed to test pricing calculation.",
                variant: "destructive",
            });
        } finally {
            setTesting(false);
        }
    };

    const handleClose = () => {
        if (hasChanges) {
            // Could add unsaved changes warning here
        }
        setTestResult(null);
        onClose();
    };

    return (
        <>
            <Dialog open={isOpen} onOpenChange={handleClose}>
                <DialogContent className="sm:max-w-3xl flex flex-col max-h-[90vh] p-0 gap-0 overflow-hidden">
                    <DialogHeader className="p-6 pb-4 border-b shrink-0 bg-gradient-to-r from-blue-50 to-indigo-50">
                        <div className="flex items-start justify-between">
                            <div>
                                <DialogTitle className="flex items-center gap-2 text-xl">
                                    <Calculator className="h-5 w-5 text-blue-600" />
                                    Pricing Configuration
                                </DialogTitle>
                                <DialogDescription className="mt-1">
                                    Configure custom pricing formula for <span className="font-medium text-foreground">{categoryName}</span>
                                </DialogDescription>
                            </div>
                            {configData && (
                                <Badge
                                    variant={configData.hasCustomConfig ? "default" : "secondary"}
                                    className={cn(
                                        "ml-4 shrink-0",
                                        configData.hasCustomConfig
                                            ? "bg-blue-100 text-blue-700 border-blue-200"
                                            : "bg-gray-100 text-gray-600 border-gray-200"
                                    )}
                                >
                                    {configData.hasCustomConfig ? (
                                        <><Settings2 className="h-3 w-3 mr-1" /> Custom Config</>
                                    ) : (
                                        <><Globe className="h-3 w-3 mr-1" /> Using Global</>
                                    )}
                                </Badge>
                            )}
                        </div>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        {loading ? (
                            <div className="space-y-6">
                                <div className="grid grid-cols-2 gap-4">
                                    {Array.from({ length: 5 }).map((_, i) => (
                                        <div key={i} className="space-y-2">
                                            <Skeleton className="h-4 w-24" />
                                            <Skeleton className="h-10 w-full" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Global Config Info */}
                                {configData && !configData.hasCustomConfig && (
                                    <div className="p-4 bg-blue-50/50 border border-blue-100 rounded-lg">
                                        <div className="flex items-start gap-3">
                                            <Info className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                                            <div>
                                                <p className="text-sm font-medium text-blue-900">Using Global Configuration</p>
                                                <p className="text-xs text-blue-700 mt-1">
                                                    This category is using the global pricing formula. Set custom values below to override.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Pricing Formula Fields */}
                                <Card>
                                    <CardHeader className="pb-4">
                                        <CardTitle className="text-base flex items-center gap-2">
                                            <TrendingUp className="h-4 w-4 text-blue-600" />
                                            Pricing Formula Parameters
                                        </CardTitle>
                                        <CardDescription>
                                            Configure how transport fees are calculated for products in this category.
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            {/* Per Km Rate */}
                                            <div className="space-y-2">
                                                <Label htmlFor="perKmRate" className="flex items-center gap-2">
                                                    <Truck className="h-4 w-4 text-muted-foreground" />
                                                    Per Km Rate (₹) <span className="text-red-500">*</span>
                                                </Label>
                                                <Input
                                                    id="perKmRate"
                                                    type="number"
                                                    step="0.1"
                                                    min="0.1"
                                                    value={formData.perKmRate}
                                                    onChange={(e) => handleInputChange('perKmRate', e.target.value)}
                                                    className={cn(errors.perKmRate && "border-red-500")}
                                                    placeholder="e.g., 2.5"
                                                />
                                                {errors.perKmRate && (
                                                    <p className="text-xs text-red-500 flex items-center gap-1">
                                                        <AlertCircle className="h-3 w-3" />
                                                        {errors.perKmRate}
                                                    </p>
                                                )}
                                                <p className="text-xs text-muted-foreground">
                                                    Base rate per kilometer for transport fee
                                                </p>
                                            </div>

                                            {/* Minimum Charge */}
                                            <div className="space-y-2">
                                                <Label htmlFor="minimumCharge" className="flex items-center gap-2">
                                                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                                                    Minimum Charge (₹)
                                                </Label>
                                                <Input
                                                    id="minimumCharge"
                                                    type="number"
                                                    step="1"
                                                    min="0"
                                                    value={formData.minimumCharge ?? 0}
                                                    onChange={(e) => handleInputChange('minimumCharge', e.target.value)}
                                                    className={cn(errors.minimumCharge && "border-red-500")}
                                                    placeholder="e.g., 50"
                                                />
                                                {errors.minimumCharge && (
                                                    <p className="text-xs text-red-500 flex items-center gap-1">
                                                        <AlertCircle className="h-3 w-3" />
                                                        {errors.minimumCharge}
                                                    </p>
                                                )}
                                                <p className="text-xs text-muted-foreground">
                                                    Minimum transport charge regardless of distance
                                                </p>
                                            </div>

                                            {/* Maximum Charge */}
                                            <div className="space-y-2">
                                                <Label htmlFor="maximumCharge" className="flex items-center gap-2">
                                                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                                                    Maximum Charge (₹)
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p className="max-w-xs text-xs">Leave empty or set to 0 for no maximum cap</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                </Label>
                                                <Input
                                                    id="maximumCharge"
                                                    type="number"
                                                    step="1"
                                                    min="0"
                                                    value={formData.maximumCharge ?? ''}
                                                    onChange={(e) => handleInputChange('maximumCharge', e.target.value || 'null')}
                                                    className={cn(errors.maximumCharge && "border-red-500")}
                                                    placeholder="No cap"
                                                />
                                                {errors.maximumCharge && (
                                                    <p className="text-xs text-red-500 flex items-center gap-1">
                                                        <AlertCircle className="h-3 w-3" />
                                                        {errors.maximumCharge}
                                                    </p>
                                                )}
                                                <p className="text-xs text-muted-foreground">
                                                    Maximum transport charge cap (leave empty for no cap)
                                                </p>
                                            </div>

                                            {/* Fuel Surcharge */}
                                            <div className="space-y-2">
                                                <Label htmlFor="fuelSurchargePercentage" className="flex items-center gap-2">
                                                    <Fuel className="h-4 w-4 text-muted-foreground" />
                                                    Fuel Surcharge (%)
                                                </Label>
                                                <Input
                                                    id="fuelSurchargePercentage"
                                                    type="number"
                                                    step="0.5"
                                                    min="0"
                                                    max="100"
                                                    value={formData.fuelSurchargePercentage ?? 0}
                                                    onChange={(e) => handleInputChange('fuelSurchargePercentage', e.target.value)}
                                                    className={cn(errors.fuelSurchargePercentage && "border-red-500")}
                                                    placeholder="e.g., 5"
                                                />
                                                {errors.fuelSurchargePercentage && (
                                                    <p className="text-xs text-red-500 flex items-center gap-1">
                                                        <AlertCircle className="h-3 w-3" />
                                                        {errors.fuelSurchargePercentage}
                                                    </p>
                                                )}
                                                <p className="text-xs text-muted-foreground">
                                                    Additional percentage added for fuel costs (0-100%)
                                                </p>
                                            </div>

                                            {/* Rush Hour Multiplier */}
                                            <div className="space-y-2 sm:col-span-2">
                                                <Label htmlFor="rushHourMultiplier" className="flex items-center gap-2">
                                                    <Clock className="h-4 w-4 text-muted-foreground" />
                                                    Rush Hour Multiplier
                                                </Label>
                                                <Input
                                                    id="rushHourMultiplier"
                                                    type="number"
                                                    step="0.1"
                                                    min="1"
                                                    max="5"
                                                    value={formData.rushHourMultiplier ?? 1}
                                                    onChange={(e) => handleInputChange('rushHourMultiplier', e.target.value)}
                                                    className={cn("sm:max-w-[200px]", errors.rushHourMultiplier && "border-red-500")}
                                                    placeholder="e.g., 1.2"
                                                />
                                                {errors.rushHourMultiplier && (
                                                    <p className="text-xs text-red-500 flex items-center gap-1">
                                                        <AlertCircle className="h-3 w-3" />
                                                        {errors.rushHourMultiplier}
                                                    </p>
                                                )}
                                                <p className="text-xs text-muted-foreground">
                                                    Multiplier applied during rush hours (1 = no additional charge)
                                                </p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Live Formula Preview */}
                                <div className="p-4 bg-gradient-to-r from-indigo-50/50 to-purple-50/50 border border-indigo-100 rounded-lg">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Calculator className="h-4 w-4 text-indigo-600" />
                                        <span className="text-sm font-medium text-indigo-900">Formula Preview</span>
                                    </div>
                                    <div className="space-y-2 font-mono text-xs">
                                        <div className="p-2 bg-white/60 rounded border border-indigo-100">
                                            <span className="text-muted-foreground">Base Transport = </span>
                                            <span className="text-indigo-700 font-medium">{formData.perKmRate}</span>
                                            <span className="text-muted-foreground"> × distance × quantity</span>
                                        </div>
                                        {(formData.fuelSurchargePercentage ?? 0) > 0 && (
                                            <div className="p-2 bg-white/60 rounded border border-indigo-100">
                                                <span className="text-muted-foreground">+ Fuel Surcharge = </span>
                                                <span className="text-orange-600 font-medium">{formData.fuelSurchargePercentage}%</span>
                                                <span className="text-muted-foreground"> of base</span>
                                            </div>
                                        )}
                                        {(formData.rushHourMultiplier ?? 1) > 1 && (
                                            <div className="p-2 bg-white/60 rounded border border-indigo-100">
                                                <span className="text-muted-foreground">× Rush Hour = </span>
                                                <span className="text-purple-600 font-medium">{formData.rushHourMultiplier}x</span>
                                            </div>
                                        )}
                                        <div className="p-2 bg-white/60 rounded border border-indigo-100 flex flex-wrap gap-4">
                                            <div>
                                                <span className="text-muted-foreground">Min: </span>
                                                <span className="text-green-600 font-medium">₹{formData.minimumCharge ?? 0}</span>
                                            </div>
                                            <div>
                                                <span className="text-muted-foreground">Max: </span>
                                                <span className="text-red-600 font-medium">
                                                    {formData.maximumCharge ? `₹${formData.maximumCharge}` : 'No cap'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <Separator />

                                {/* Test Calculator */}
                                <Card>
                                    <CardHeader className="pb-4">
                                        <CardTitle className="text-base flex items-center gap-2">
                                            <TestTube className="h-4 w-4 text-green-600" />
                                            Test Calculator
                                        </CardTitle>
                                        <CardDescription>
                                            Test your pricing formula with sample values before saving.
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="testDistance">Distance (km)</Label>
                                                <Input
                                                    id="testDistance"
                                                    type="number"
                                                    min="1"
                                                    value={testInputs.distance}
                                                    onChange={(e) => setTestInputs(prev => ({ ...prev, distance: parseFloat(e.target.value) || 0 }))}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="testQuantity">Quantity</Label>
                                                <Input
                                                    id="testQuantity"
                                                    type="number"
                                                    min="1"
                                                    value={testInputs.quantity}
                                                    onChange={(e) => setTestInputs(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 1 }))}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="testMaterialPrice">Material Price (₹)</Label>
                                                <Input
                                                    id="testMaterialPrice"
                                                    type="number"
                                                    min="0"
                                                    value={testInputs.materialPrice}
                                                    onChange={(e) => setTestInputs(prev => ({ ...prev, materialPrice: parseFloat(e.target.value) || 0 }))}
                                                />
                                            </div>
                                        </div>

                                        <Button
                                            variant="outline"
                                            onClick={handleTestCalculation}
                                            disabled={testing}
                                            className="w-full sm:w-auto"
                                        >
                                            {testing ? (
                                                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Calculating...</>
                                            ) : (
                                                <><Calculator className="h-4 w-4 mr-2" /> Calculate</>
                                            )}
                                        </Button>

                                        {/* Test Results */}
                                        {testResult && (
                                            <div className="mt-4 p-4 bg-green-50/50 border border-green-100 rounded-lg space-y-4">
                                                <div className="flex items-center gap-2 text-green-700">
                                                    <CheckCircle2 className="h-4 w-4" />
                                                    <span className="font-medium text-sm">Calculation Result</span>
                                                    <Badge variant="outline" className="ml-auto text-xs">
                                                        {testResult.configUsed.source === 'category' ? 'Custom Config' : 'Global Config'}
                                                    </Badge>
                                                </div>

                                                {/* Formula Steps */}
                                                <div className="space-y-2 text-sm">
                                                    <p className="text-muted-foreground font-mono text-xs">{testResult.formula.step1}</p>
                                                    {testResult.formula.step2 && (
                                                        <p className="text-muted-foreground font-mono text-xs">{testResult.formula.step2}</p>
                                                    )}
                                                    {testResult.formula.step3 && (
                                                        <p className="text-muted-foreground font-mono text-xs">{testResult.formula.step3}</p>
                                                    )}
                                                    {testResult.formula.step4 && (
                                                        <p className="text-muted-foreground font-mono text-xs">{testResult.formula.step4}</p>
                                                    )}
                                                    <p className="text-green-700 font-mono text-xs font-medium">{testResult.formula.step5}</p>
                                                </div>

                                                <Separator className="bg-green-200" />

                                                {/* Summary */}
                                                <div className="grid grid-cols-3 gap-4 text-center">
                                                    <div>
                                                        <p className="text-xs text-muted-foreground">Material Fee</p>
                                                        <p className="text-lg font-bold text-foreground">₹{testResult.calculation.materialFeeTotal.toLocaleString()}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-muted-foreground">Transport Fee</p>
                                                        <p className="text-lg font-bold text-blue-600">₹{testResult.calculation.transportFeeTotal.toLocaleString()}</p>
                                                        {testResult.calculation.cappedBy && (
                                                            <Badge variant="outline" className="text-xs mt-1">
                                                                Capped by {testResult.calculation.cappedBy === 'minimumCharge' ? 'Min' : 'Max'}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-muted-foreground">Total Price</p>
                                                        <p className="text-lg font-bold text-green-600">₹{testResult.calculation.totalPrice.toLocaleString()}</p>
                                                    </div>
                                                </div>

                                                <div className="text-center text-xs text-muted-foreground">
                                                    Per unit: Material ₹{testResult.calculation.perUnit.materialFee.toFixed(2)} +
                                                    Transport ₹{testResult.calculation.perUnit.transportFee.toFixed(2)} =
                                                    <span className="font-medium text-foreground"> ₹{testResult.calculation.perUnit.totalPrice.toFixed(2)}</span>
                                                </div>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </>
                        )}
                    </div>

                    <DialogFooter className="p-6 pt-4 border-t bg-muted/30 mt-auto shrink-0 gap-2 flex-col sm:flex-row">
                        {configData?.hasCustomConfig && (
                            <Button
                                variant="outline"
                                onClick={() => setShowResetDialog(true)}
                                disabled={saving || loading}
                                className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 border-orange-200"
                            >
                                <RotateCcw className="h-4 w-4 mr-2" />
                                Reset to Global
                            </Button>
                        )}
                        <div className="flex gap-2 ml-auto">
                            <Button variant="outline" onClick={handleClose} disabled={saving}>
                                Cancel
                            </Button>
                            <Button onClick={handleSave} disabled={saving || loading}>
                                {saving ? (
                                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                                ) : (
                                    <><Save className="h-4 w-4 mr-2" /> Save Configuration</>
                                )}
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reset Confirmation Dialog */}
            <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Reset to Global Configuration?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will remove the custom pricing configuration for <span className="font-semibold">{categoryName}</span> and
                            revert to using the global pricing formula. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleReset}
                            disabled={saving}
                            className="bg-orange-600 hover:bg-orange-700"
                        >
                            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RotateCcw className="h-4 w-4 mr-2" />}
                            Reset to Global
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

export default PricingConfigModal;
